// Where a dynamics run's sensors sit, and how lit each one is (spec §15, Electrodes): its forward
// model's 10–20 scalp sites and depth contacts, by channel. A run re-observed from another
// (`reobservation.source_id`, as the distributed run is) keeps that run's sensors. Positions are the
// run's surface RAS (fsaverage), which the app draws in the scene's MNI space as design's screens
// do: the two are within a few mm.

import { SimFiles, type SimSource } from './files.ts';
import { locate, sampleAt, type TimeAxis } from './time.ts';
import type { DynamicsSim } from './dynamics.ts';
import type { PatientComparison } from './comparison.ts';

export type SensorKind = 'eeg' | 'seeg';
/** The model's sensors, and a real patient's own contacts. */
export type SiteKind = SensorKind | 'contacts';
export type Vec3 = [number, number, number];

/** A drawn site's selection id, `electrode:<kind>:<channel>`, as a cells run's cell is `cell:<id>`. */
export const siteId = (kind: SiteKind, name: string): string => `electrode:${kind}:${name}`;

/** A site's kind and channel from its selection id; null for any other id. */
export function parseSiteId(id: string | null | undefined): { kind: SiteKind; name: string } | null {
  const m = id ? /^electrode:(eeg|seeg|contacts):(.+)$/.exec(id) : null;
  return m ? { kind: m[1] as SiteKind, name: m[2]! } : null;
}

export interface SensorSites {
  names: string[];
  positions: Vec3[];
}

/** The forward model's sensor tables (dynamics' forward.json); other keys are left alone. */
interface Forward {
  eeg?: { channel_names?: string[]; positions_surface_ras_mm?: number[][]; model?: string; reference?: string };
  seeg?: {
    contact_names?: string[];
    contact_positions_surface_ras_mm?: number[][];
    model?: string;
    /** How the contacts were placed ('Four hand-defined straight depth shafts …'), and what they are. */
    contacts?: string;
    contact_kind?: string;
    potential_reference?: string;
    /** The FreeSurfer aseg label of each contact's nearest voxel, in `contact_names` order. */
    nearest_voxel_aseg_ids?: number[];
  };
}

const isVec3 = (p: unknown): p is Vec3 => Array.isArray(p) && p.length === 3 && p.every((x) => typeof x === 'number' && Number.isFinite(x));

/** One kind's sites in the run's channel order; a channel without a position is left out. */
export function sitesFrom(names: readonly string[], posNames: readonly string[] | undefined, pos: readonly unknown[] | undefined): SensorSites | null {
  if (!posNames || !pos || posNames.length !== pos.length) return null;
  const at = new Map<string, Vec3>();
  posNames.forEach((n, i) => {
    const p = pos[i];
    if (isVec3(p)) at.set(n, p);
  });
  const out: SensorSites = { names: [], positions: [] };
  for (const n of names) {
    const p = at.get(n);
    if (!p) continue;
    out.names.push(n);
    out.positions.push(p);
  }
  return out.names.length ? out : null;
}

/** Where a run's forward model may be: its own directory, then its source run's (`reobservation.source_id`). */
function forwardDirs(path: string, sim: DynamicsSim): string[] {
  const source = (sim.header as { reobservation?: { source_id?: unknown } }).reobservation?.source_id;
  const parent = path.includes('/') ? path.slice(0, path.lastIndexOf('/') + 1) : '';
  return [path, ...(typeof source === 'string' && source ? [parent + source] : [])];
}

/** The run's sensor sites by kind: its own forward.json, else its source run's. */
export async function sensorSites(src: SimSource, path: string, sim: DynamicsSim): Promise<Partial<Record<SensorKind, SensorSites>>> {
  for (const dir of forwardDirs(path, sim)) {
    // The listing says first, so a run without a forward model (the HUP fits) costs no failing request.
    const fwd = await new SimFiles(src, dir).maybeJson<Forward>('forward.json').catch(() => null);
    if (!fwd) continue;
    const eeg = sitesFrom(sim.channels('eeg'), fwd.eeg?.channel_names, fwd.eeg?.positions_surface_ras_mm);
    const seeg = sitesFrom(sim.channels('seeg'), fwd.seeg?.contact_names, fwd.seeg?.contact_positions_surface_ras_mm);
    if (eeg || seeg) return { ...(eeg ? { eeg } : {}), ...(seeg ? { seeg } : {}) };
  }
  return {};
}

/** σ of the distance weighting, mm (spec §15). */
export const SITE_SIGMA_MM = 20;

/**
 * Each site's level, 0–1: the cortex activity the scene paints under it, its cortical parcels
 * weighted by distance (σ 20 mm) and normalised per site, each parcel on the run's display range.
 * Design's screens light their sites the same way (design/scripts/screens_data.py).
 */
export class SiteLevels {
  private readonly w: Float64Array;
  private readonly cols: number[];

  constructor(
    readonly sites: readonly Vec3[],
    centres: ArrayLike<number>,
    cortical: readonly boolean[],
    private readonly activity: ArrayLike<number>,
    private readonly stride: number,
    private readonly axis: TimeAxis,
    private readonly top: number,
    sigma = SITE_SIGMA_MM,
  ) {
    this.cols = cortical.flatMap((c, p) => (c ? [p] : []));
    const P = this.cols.length;
    this.w = new Float64Array(sites.length * P);
    sites.forEach((s, i) => {
      const d2 = this.cols.map((p) => (s[0] - centres[3 * p]!) ** 2 + (s[1] - centres[3 * p + 1]!) ** 2 + (s[2] - centres[3 * p + 2]!) ** 2);
      // From the nearest parcel's distance, so a site far from every parcel still weighs them (the
      // normalised weights are the same).
      const near = Math.min(...d2);
      let sum = 0;
      d2.forEach((d, j) => {
        const w = Math.exp(-(d - near) / (2 * sigma * sigma));
        this.w[i * P + j] = w;
        sum += w;
      });
      for (let j = 0; j < P; j++) this.w[i * P + j]! /= sum;
    });
  }

  at(t: number, out = new Float32Array(this.sites.length)): Float32Array {
    const P = this.cols.length;
    const a = this.cols.map((p) => Math.min(1, Math.max(0, sampleAt(this.activity, this.stride, p, this.axis, t) / this.top)));
    for (let i = 0; i < this.sites.length; i++) {
      let v = 0;
      for (let j = 0; j < P; j++) v += this.w[i * P + j]! * a[j]!;
      out[i] = v;
    }
    return out;
  }
}

/** A run's site levels: its parcel centres and activity, its cortical parcels (`dk_name` ctx-…). */
export async function siteLevels(sim: DynamicsSim, sites: SensorSites): Promise<SiteLevels | null> {
  if (!sim.has('parcel_activity') || !sim.has('parcel_centres')) return null;
  const P = sim.parcels.length;
  const [centres, activity] = await Promise.all([sim.array('parcel_centres'), sim.array('parcel_activity')]);
  if (centres.length !== 3 * P || !P) return null;
  const cortical = sim.parcels.map((p) => /^ctx-/.test((p as { dk_name?: string }).dk_name ?? ''));
  if (!cortical.some(Boolean)) return null;
  const top = sim.activityRange?.[1] ?? 1;
  return new SiteLevels(sites.positions, centres, cortical, activity, P, sim.axisOf('parcel_activity'), top > 0 ? top : 1);
}

/** Sites and how lit each is at a time, 0–1: the model's sensors (SiteLevels) or a patient's contacts. */
export interface Levels {
  readonly sites: readonly Vec3[];
  at(t: number, out?: Float32Array): Float32Array;
}

/** A real patient's own contacts (the HUP fits): each bipolar channel at its pair's midpoint, from
 *  observation.json, and whether the clinicians marked it in the seizure onset zone. */
export interface PatientSites extends SensorSites {
  soz: boolean[];
}

export function patientSites(c: PatientComparison): PatientSites | null {
  const out: PatientSites = { names: [], positions: [], soz: [] };
  for (const k of c.contacts) {
    if (!k.position) continue;
    out.names.push(k.name);
    out.positions.push(k.position);
    out.soz.push(k.soz);
  }
  return out.names.length ? out : null;
}

/**
 * Each of a patient's contacts lit by their own recording: its envelope (the line length as a robust z)
 * at the scene time less the model's onset, where the traces line the two up, on the recording's
 * display scale (its 99.5th percentile over every contact), so the clinical seizure lights them as it
 * spread through this person.
 */
export class RecordedLevels implements Levels {
  readonly sites: Vec3[];
  private readonly cols: number[];

  constructor(private readonly c: PatientComparison) {
    const placed = c.contacts.filter((k) => k.position);
    this.sites = placed.map((k) => k.position!);
    this.cols = placed.map((k) => k.index);
  }

  at(t: number, out = new Float32Array(this.sites.length)): Float32Array {
    const e = this.c.observed;
    const C = this.c.contacts.length;
    for (let i = 0; i < this.cols.length; i++) {
      const v = sampleAt(e.data, C, this.cols[i]!, e.axis, t - this.c.onset) / this.c.scale.observed;
      out[i] = Math.min(1, Math.max(0, v));
    }
    return out;
  }
}


/** What the inspector says of a model sensor, from the forward model that placed it. */
export interface SensorInfo {
  /** The forward solution ('MNE-Python make_forward_solution, fsaverage 3-layer …'). */
  model: string | null;
  /** What it's referenced to ('common average over these 32 channels'). */
  reference: string | null;
  /** How a depth contact was placed; and whether by hand (`contact_kind` artist-made), not from an implant. */
  placement: string | null;
  byHand: boolean;
  /** A depth contact's nearest voxel's FreeSurfer aseg label. */
  aseg: number | null;
}

const text = (x: unknown): string | null => (typeof x === 'string' && x.trim() ? x.trim() : null);

/** A model sensor's forward model and reference: the run's own header first (`observation.references`), then forward.json. */
export async function sensorInfo(src: SimSource, path: string, sim: DynamicsSim, kind: SensorKind, name: string): Promise<SensorInfo> {
  const refs = (sim.header.observation as { references?: Record<string, unknown> } | undefined)?.references;
  for (const dir of forwardDirs(path, sim)) {
    const fwd = await new SimFiles(src, dir).maybeJson<Forward>('forward.json').catch(() => null);
    if (kind === 'eeg' && fwd?.eeg?.channel_names?.includes(name)) {
      return { model: text(fwd.eeg.model), reference: text(refs?.eeg) ?? text(fwd.eeg.reference), placement: null, byHand: false, aseg: null };
    }
    const i = kind === 'seeg' ? (fwd?.seeg?.contact_names?.indexOf(name) ?? -1) : -1;
    if (fwd?.seeg && i >= 0) {
      const aseg = fwd.seeg.nearest_voxel_aseg_ids?.[i];
      return {
        model: text(fwd.seeg.model),
        reference: text(refs?.seeg) ?? text(fwd.seeg.potential_reference),
        placement: text(fwd.seeg.contacts),
        byHand: /artist|hand/i.test(fwd.seeg.contact_kind ?? ''),
        aseg: typeof aseg === 'number' && Number.isInteger(aseg) ? aseg : null,
      };
    }
  }
  return { model: null, reference: text(refs?.[kind]), placement: null, byHand: false, aseg: null };
}

/** FreeSurfer's aseg labels (FreeSurferColorLUT) a contact's nearest voxel may have, in words. */
const ASEG: Record<number, string> = {
  2: 'cerebral white matter',
  3: 'cerebral cortex',
  4: 'lateral ventricle',
  5: 'inferior lateral ventricle',
  7: 'cerebellar white matter',
  8: 'cerebellar cortex',
  10: 'thalamus',
  11: 'caudate',
  12: 'putamen',
  13: 'pallidum',
  17: 'hippocampus',
  18: 'amygdala',
  26: 'accumbens',
  28: 'ventral diencephalon',
  31: 'choroid plexus',
};
/** The right hemisphere's labels, by the left's. */
const ASEG_RIGHT: Record<number, number> = { 41: 2, 42: 3, 43: 4, 44: 5, 46: 7, 47: 8, 49: 10, 50: 11, 51: 12, 52: 13, 53: 17, 54: 18, 58: 26, 60: 28, 63: 31 };
/** The parcels the dynamics runs name by aseg structure (`source_name`, after the side). */
const ASEG_PARCEL: Record<number, string> = { 10: 'Thalamus_Proper', 11: 'Caudate', 12: 'Putamen', 13: 'Pallidum', 17: 'Hippocampus', 18: 'Amygdala', 26: 'Accumbens_area' };
const ASEG_MIDLINE: Record<number, string> = { 14: 'Third ventricle', 15: 'Fourth ventricle', 16: 'Brainstem', 24: 'CSF' };

/** An aseg label in words ('Left hippocampus'), and the run's parcel for it where it models one ('Left_Hippocampus'). */
export function asegLabel(id: number): { name: string; parcel: string | null } | null {
  if (ASEG_MIDLINE[id]) return { name: ASEG_MIDLINE[id], parcel: null };
  const left = ASEG_RIGHT[id] ?? id;
  const words = ASEG[left];
  if (!words) return null;
  const side = ASEG_RIGHT[id] ? 'Right' : 'Left';
  return { name: `${side} ${words}`, parcel: ASEG_PARCEL[left] ? `${side}_${ASEG_PARCEL[left]}` : null };
}

/** One channel of a `[sample][channel]` array. */
export function column(data: ArrayLike<number>, C: number, ch: number): Float32Array {
  const n = C > 0 ? Math.floor(data.length / C) : 0;
  const y = new Float32Array(n);
  for (let i = 0; i < n; i++) y[i] = data[i * C + ch]!;
  return y;
}

export const mean = (y: ArrayLike<number>): number => {
  let m = 0;
  for (let i = 0; i < y.length; i++) m += y[i]!;
  return y.length ? m / y.length : 0;
};

/** RMS of `y` about `centre` over the `windowS` seconds up to `t` on its axis; null where that's under two samples. */
export function rmsBefore(y: ArrayLike<number>, axis: TimeAxis, t: number, centre: number, windowS = 1): number | null {
  const a = locate(axis, t - windowS);
  const b = locate(axis, t);
  const first = a.w > 0 ? a.i1 : a.i0;
  const last = b.w >= 1 - 1e-9 ? b.i1 : b.i0;
  if (last - first < 1) return null;
  let s = 0;
  for (let i = first; i <= last; i++) s += (y[i]! - centre) ** 2;
  return Math.sqrt(s / (last - first + 1));
}

/**
 * Where a model channel's signal comes from. The run's sensors are linear in its parcel signals
 * (`header.observation.equation`: sensor[t, c] = Σp signal[t, p] · scale[p] · gain[c, p]), so the
 * channel about its mean is Σp yp, each parcel's part yp = wp (xp − x̄p) with wp = scale[p] · gain[c, p],
 * and its variance over the run splits exactly into the parts' covariances with it: each share is
 * cov(yp, y) / var(y). They sum to 1; a part that cancels the others' takes a negative share.
 * `channel`, the run's own channel, checks the sum: `r2` is how much of its variance the parts
 * explain (1 where the run's sensors are its parcels through the gain and nothing more).
 */
export function varianceShares(signal: ArrayLike<number>, P: number, w: ArrayLike<number>, channel?: ArrayLike<number>): { shares: Float64Array; r2: number | null } {
  const T = P > 0 ? Math.floor(signal.length / P) : 0;
  const xm = new Float64Array(P);
  for (let t = 0; t < T; t++) for (let p = 0; p < P; p++) xm[p]! += signal[t * P + p]!;
  for (let p = 0; p < P; p++) xm[p]! /= T || 1;
  const cm = channel && channel.length === T ? mean(channel) : null;
  const cov = new Float64Array(P);
  let vy = 0;
  let vc = 0;
  let yc = 0;
  for (let t = 0; t < T; t++) {
    let y = 0;
    for (let p = 0; p < P; p++) y += w[p]! * (signal[t * P + p]! - xm[p]!);
    vy += y * y;
    for (let p = 0; p < P; p++) cov[p]! += w[p]! * (signal[t * P + p]! - xm[p]!) * y;
    if (cm !== null) {
      const c = channel![t]! - cm;
      vc += c * c;
      yc += y * c;
    }
  }
  const shares = new Float64Array(P);
  if (vy > 0) for (let p = 0; p < P; p++) shares[p] = cov[p]! / vy;
  return { shares, r2: cm !== null && vy > 0 && vc > 0 ? (yc * yc) / (vy * vc) : null };
}

/** A parcel's share of a channel's variance. */
export interface ChannelSource {
  parcel: number;
  share: number;
}

/** Below this, a channel isn't its parcels through the gain alone (a run that adds sensor noise, say), so no shares are shown. */
const EXPLAINED = 0.99;

/**
 * A model channel's parcels by their share of its variance over the run (`varianceShares`), largest
 * first. Null where the run has no gain for its kind or its parcel signals aren't on the channel's time
 * base, or where the parts explain less than 99 % of the channel's own variance.
 */
export async function channelSources(sim: DynamicsSim, kind: SensorKind, ch: number, channel: ArrayLike<number>): Promise<ChannelSource[] | null> {
  const g = `${kind}_gain`;
  const d = sim.header.arrays;
  const P = sim.parcels.length;
  const C = sim.channels(kind).length;
  if (!d?.[g] || !d.parcel_signal || !P || ch < 0 || ch >= C) return null;
  if (d[g].shape[0] !== C || d[g].shape[1] !== P || d.parcel_signal.shape[1] !== P || d.parcel_signal.shape[0] !== channel.length) return null;
  const [gain, signal, scale] = await Promise.all([sim.array(g), sim.array('parcel_signal'), d.source_scale ? sim.array('source_scale') : null]);
  if (scale && scale.length !== P) return null;
  const w = Float64Array.from({ length: P }, (_, p) => gain[ch * P + p]! * (scale ? scale[p]! : 1));
  const { shares, r2 } = varianceShares(signal, P, w, channel);
  if (r2 === null || r2 < EXPLAINED) return null;
  return Array.from(shares, (share, parcel) => ({ parcel, share })).sort((a, b) => b.share - a.share);
}
